import React, { useState, useEffect, useRef } from "react"
import { useMoney } from "../../hooks/useMoney"
import { useAppDispatch, useAppSelector } from "../../store/hook"
import {
  updateProgram,
  fetchProgramList,
  provisionMypForProgram,
  Program,
  BankDetails,
  Bank,
  CreateProgramPayload,
  Processor,
} from "../../features/program/ProgramSlice"
import { X } from "lucide-react"
import { Principal } from "../../features/principal/PrincipalSlice"
import { fetchPurposeCodes, fetchPurposeCodeByCode } from "../../features/purposeCode/PurposeCodeSlice"
import { EncryptionUtil } from "../../utils/encryption"
import { validateBankDetails, renderBankDetailsSection, getProcessorBrand } from "./BankDetailsSections"
import { toIciciCbdcLocationPayload, fromIciciCbdcLocationPayload } from "../../data/locationCodes"

/* --------------------------------- Helper Functions ------------------------------ */

// Function to determine if a processor requires bank & redemption details
const requiresBankDetails = (processorType: string) => {
  const brand = getProcessorBrand(processorType)
  return brand === 'ICICI' || brand === 'YESBANK' || brand === 'ICICI_CBDC' || brand === 'MYP'
}

// The four bank-integrated brands (ICICI, YESBANK, ICICI_CBDC, MYP) fork on
// issuance lifecycle as a real runtime choice (direct vs indirect) - for
// MYP this is VMS's own issuanceType and nothing else; MYP has no separate
// activation concept of its own (it's always told activationMode: DIRECT
// server-side - see programs/src/services/program.service.js#buildMypProgramFields).
// BLOCKCHAIN has a fixed lifecycle (always indirect - it never calls a real
// bank) rather than a per-program choice, but still needs the field
// visible/correct here, unlike SELP (always direct) which has no lifecycle
// concept worth surfacing at all.
const brandHasIssuanceType = (processorType: string) => {
  const brand = getProcessorBrand(processorType)
  return requiresBankDetails(processorType) || brand === 'BLOCKCHAIN'
}

const isIciciCbdcProcessor = (processorType: string) => {
  return getProcessorBrand(processorType) === 'ICICI_CBDC'
}

// PATCH/PUT /programs/{id} replaces bankDetails wholesale (see
// programs/src/services/program.service.js#updateProgram) - carries forward
// mypProgramId/mypProvisionStatus/mypProvisionError from the program as
// loaded, since they're written by the backend at provisioning time and
// aren't editable fields here. Losing mypProgramId would silently break
// voucher issuance for this program (myp.processor.js#activateVouchersForOrder
// requires it). Shared by the normal "Update Program" submit and by
// handleProvisionClick's save-before-resync step, so both send the exact
// same shape.
const buildMypCleanBankDetails = (formBankDetails: BankDetails, currentBankDetails?: BankDetails): BankDetails => {
  const cleanBankDetails: BankDetails = {
    programType: formBankDetails.programType || "LONG_RUNNING",
    expiry: formBankDetails.expiry,
    limits: formBankDetails.limits,
    mypProgramId: currentBankDetails?.mypProgramId,
    mypProvisionStatus: currentBankDetails?.mypProvisionStatus,
    mypProvisionError: currentBankDetails?.mypProvisionError,
  }
  if (formBankDetails.merchantRules?.mode === 'RESTRICTED') {
    cleanBankDetails.merchantRules = formBankDetails.merchantRules
  }
  if (formBankDetails.lowFloatThreshold) {
    cleanBankDetails.lowFloatThreshold = formBankDetails.lowFloatThreshold
  }
  return cleanBankDetails
}

/* --------------------------------- Interfaces ------------------------------ */

interface FormErrors {
  programCode?: string
  programName?: string
  displayName?: string
  description?: string
  logo?: string
  brandImage?: string
  voucherImage?: string
  currency?: string
  processorType?: string
  issuanceType?: string
  tagline?: string
  callToAction?: string
  brandImageAlt?: string
  maximumAmount?: string
  minimumAmount?: string
  issuerBankId?: string
  redemptionType?: string
  merchantId?: string
  subMerchantId?: string
  period?: string
  bankDetailsMcc?: string
  bankDetailsPayerVa?: string
  bankDetailsPurposeCode?: string
  bankDetailsMandateType?: string
  bankDetailsAccessKey?: string
  bankDetailsPayeeVpa?: string
  // YESBANK specific error fields
  bankDetailsMandateName?: string
  bankDetailsAmountRule?: string
  bankDetailsVoucherCategory?: string
  bankDetailsRecurrencePattern?: string
  bankDetailsPayerRevocable?: string
  bankDetailsClientIdentifier?: string
  bankDetailsPreAuth?: string
  bankDetailsRefurl?: string
  receiverHowToUseInstructions?: string
  standardTerms?: string
  denominationType?: string
  cardDescription?: string
  receiverInstructions?: string
  specificTerms?: string
  denominationMaxAmount?: string
  denominationMinAmount?: string
  denominationAmount?: string
}

interface EditProgramsProps {
  principal: Principal
  showComponent: (show: null | string) => void
  programId: string
  selectedProgram: Program
  bankList: Bank[]
  processorList: Processor[]
  programError: string
}

interface PurposeCodeOption {
  code: string
  description: string
  mccs: Record<string, string>
}

const EditPrograms: React.FC<EditProgramsProps> = ({
  selectedProgram,
  principal,
  showComponent,
  programId,
  bankList,
  processorList,
  programError,
}) => {
  /* --------------------------------- Redux ------------------------------ */

  const dispatch = useAppDispatch()
  const { code: lobCurrencyCode } = useMoney()
  const { purposeCodes, selectedPurposeCode: selectedPurposeCodeFromStore } = useAppSelector(state => state.purposeCode)
  const { mypProvisionLoading, mypProvisionError } = useAppSelector(state => state.program)

  /* --------------------------------- States ------------------------------ */

  const [showEncryptedAccessKey, setShowEncryptedAccessKey] = useState(false)
  const [decryptedAccessKey, setDecryptedAccessKey] = useState<string>("")
  // "Re-sync with MYP" spans two dispatches (save, then provision) - see
  // handleProvisionClick - so it needs its own loading flag rather than
  // relying on programLoading/mypProvisionLoading individually.
  const [syncingMyp, setSyncingMyp] = useState(false)

  const [formData, setFormData] = useState<CreateProgramPayload>(() => {
    // If there's a programError, try to load saved form data
    if (programError) {
      const savedData = localStorage.getItem(`editProgram_${programId}`)
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData)
          return {
            ...parsedData,
            logo: null,
            brandImage: null,
            voucherImage: null,
          }
        } catch (e) {
          console.error("Error parsing saved form data:", e)
        }
      }
    }

    // Otherwise use the default initial state
    return {
      principalId: parseInt(principal.principalId),
      programName: selectedProgram.programName,
      programCode: selectedProgram.programCode,
      description: selectedProgram.description,
      standardTerms: selectedProgram.standardTerms,
      specificTerms: selectedProgram.specificTerms,
      displayName: selectedProgram.displayName,
      brandImage: selectedProgram.brandImage,
      voucherImage: selectedProgram.voucherImage,
      logo: selectedProgram.logo,
      currency: selectedProgram.currency || lobCurrencyCode,
      processorType: selectedProgram["processor.processorId"] || "",
      processorTypeDisplay: selectedProgram["processor.processorType"] || "",
      issuanceType: selectedProgram.issuanceType || "",
      tagline: selectedProgram.tagline || "",
      cardDescription: selectedProgram.cardDescription || "",
      receiverInstructions: selectedProgram.receiverInstructions || "",
      receiverHowToUseInstructions:
        selectedProgram.receiverHowToUseInstructions || "",
      callToAction: selectedProgram.callToAction || "",
      brandImageAlt: selectedProgram.brandImageAlt || "",
      maximumAmount: Number(selectedProgram.maximumAmount) || "",
      minimumAmount: Number(selectedProgram.minimumAmount) || "",
      issuerBankId: selectedProgram.issuerBankId || null,
      redemptionType: selectedProgram.redemptionType || "single",
      merchantId: selectedProgram.merchantId || "",
      subMerchantId: selectedProgram.subMerchantId || "",
      period: Number(selectedProgram.period) || 0,
      bankDetails: selectedProgram.bankDetails ? {
        mcc: selectedProgram.bankDetails.mcc || "",
        payerVa: selectedProgram.bankDetails.payerVa || "",
        purposeCode: selectedProgram.bankDetails.purposeCode || "",
        mandateType: selectedProgram.bankDetails.mandateType || "",
        accessKey: selectedProgram.bankDetails.accessKey || "",
        payeeVpa: selectedProgram.bankDetails.payeeVpa || "",
        // YESBANK specific fields
        clientIdentifier: selectedProgram.bankDetails.clientIdentifier || "",
        mandateName: selectedProgram.bankDetails.mandateName || "",
        amountRule: selectedProgram.bankDetails.amountRule || "",
        voucherCategory: selectedProgram.bankDetails.voucherCategory || "",
        recurrencePattern: selectedProgram.bankDetails.recurrencePattern || "",
        payerRevocable: selectedProgram.bankDetails.payerRevocable || "",
        preAuth: selectedProgram.bankDetails.preAuth,
        refurl: selectedProgram.bankDetails.refurl || "",
        // ICICI CBDC specific fields. mcc above already defaults to [] for
        // this brand. Location is stored as numeric {city,state,bufferZone}
        // codes - convert back to display names for the editable dropdowns.
        sponsorId: selectedProgram.bankDetails.sponsorId || "",
        sponsorName: selectedProgram.bankDetails.sponsorName || "",
        purpose: selectedProgram.bankDetails.purpose || "",
        language: selectedProgram.bankDetails.language || "",
        location: selectedProgram.bankDetails.location
          ? fromIciciCbdcLocationPayload(selectedProgram.bankDetails.location)
          : [],
        // MYP specific fields, plus the mypProgramId/mypProvisionStatus/
        // mypProvisionError the backend writes at provisioning time - these
        // aren't edited here, only carried forward on save (see the MYP
        // branch of the submit handler below) so a re-save doesn't wipe out
        // the program's MYP linkage.
        programType: selectedProgram.bankDetails.programType || "LONG_RUNNING",
        expiry: selectedProgram.bankDetails.expiry || { mode: "DAYS_FROM_LOAD", days: "" },
        limits: selectedProgram.bankDetails.limits || {},
        merchantRules: selectedProgram.bankDetails.merchantRules || { mode: "ALL" },
        lowFloatThreshold: selectedProgram.bankDetails.lowFloatThreshold || "",
        mypProgramId: selectedProgram.bankDetails.mypProgramId,
        mypProvisionStatus: selectedProgram.bankDetails.mypProvisionStatus,
        mypProvisionError: selectedProgram.bankDetails.mypProvisionError,
      } : {
        mcc: "",
        payerVa: "",
        purposeCode: "",
        mandateType: "",
        accessKey: "",
        payeeVpa: "",
        // YESBANK specific fields
        clientIdentifier: "",
        mandateName: "",
        amountRule: "",
        voucherCategory: "",
        recurrencePattern: "",
        payerRevocable: "",
        preAuth: undefined,
        refurl: "",
        // ICICI CBDC specific fields
        sponsorId: "",
        sponsorName: "",
        purpose: "",
        language: "",
        location: [],
        programType: "LONG_RUNNING",
        expiry: { mode: "DAYS_FROM_LOAD", days: "" },
        limits: {},
        merchantRules: { mode: "ALL" },
        lowFloatThreshold: "",
      },
      denominationAmount: selectedProgram.denominationAmount || [0],
      denominationCurrency: selectedProgram.denominationCurrency || lobCurrencyCode,
      denominationMaxAmount: selectedProgram.denominationMaxAmount || null,
      denominationMinAmount: selectedProgram.denominationMinAmount || null,
      denominationType: selectedProgram.denominationType,
    }
  })

  const [purposeCodeList, setPurposeCodeList] = useState<PurposeCodeOption[]>([])
  // Tracks which purpose code's MCCs are currently being fetched, for the
  // ICICI CBDC MCC dropdown's lazy per-code loading indicator.
  const [loadingMccsForPurposeCode, setLoadingMccsForPurposeCode] = useState("")

  const selectedPurposeCode = purposeCodeList.find(
    p => p.code === formData.bankDetails.purposeCode,
  )
  const [mccList, setMccList] = useState<
    Array<{ mcc: string; description: string }>
  >(
    selectedPurposeCode?.mccs
      ? Object.keys(selectedPurposeCode.mccs).map(mcc => {
        return { mcc: mcc, description: selectedPurposeCode.mccs[mcc] }
      })
      : [],
  )
  const [formErrors, setFormErrors] = useState<FormErrors>({})


  /* ------------------------------ Hooks ---------------------------- */

  // Add useEffect for cleanup
  useEffect(() => {
    return () => {
      const { logo, brandImage, voucherImage, ...dataWithoutImages } = formData
      const dataToSave = {
        ...dataWithoutImages,
        logo: null,
        brandImage: null,
        voucherImage: null,
      }
      localStorage.setItem(`editProgram_${programId}`, JSON.stringify(dataToSave))
    }
  }, [formData, programId])

  useEffect(() => {
    dispatch(fetchPurposeCodes())
  }, [dispatch])

  useEffect(() => {
    setPurposeCodeList(
      purposeCodes.map(code => ({
        code: code.purposeCode,
        description: code.useCase,
        mccs: code.mccs || {},
      })),
    )
  }, [purposeCodes])

  // On first load, if the program already has a purpose code but no MCCs loaded yet, fetch them
  useEffect(() => {
    if (
      formData.bankDetails.purposeCode &&
      mccList.length === 0
    ) {
      dispatch(fetchPurposeCodeByCode(formData.bankDetails.purposeCode))
    }
  }, [dispatch, formData.bankDetails.purposeCode, mccList.length])

  // When a specific purpose code is fetched with its MCCs, sync both the
  // single-select mccList (used by ICICI/YESBank) and the purposeCodeList
  // entry's mccs map (used by the ICICI CBDC MCC dropdown, so the code's
  // MCCs actually show up once expanded).
  useEffect(() => {
    if (!selectedPurposeCodeFromStore || !selectedPurposeCodeFromStore.mccs) {
      return
    }

    const mccs = selectedPurposeCodeFromStore.mccs || {}
    const fetchedCode = selectedPurposeCodeFromStore.purposeCode

    if (fetchedCode === formData.bankDetails.purposeCode) {
      setMccList(
        Object.keys(mccs).map(mcc => ({
          mcc,
          description: mccs[mcc] || "",
        })),
      )
    }

    setPurposeCodeList(prev =>
      prev.map(pc => (pc.code === fetchedCode ? { ...pc, mccs } : pc)),
    )
    setLoadingMccsForPurposeCode(prev => (prev === fetchedCode ? "" : prev))
  }, [selectedPurposeCodeFromStore, formData.bankDetails.purposeCode])

  // Add useEffect to properly set processor type display from processor list
  useEffect(() => {
    if (processorList && processorList.length > 0 && selectedProgram.processorType && !formData.processorTypeDisplay) {
      const matchingProcessor = processorList.find(processor =>
        processor.processorId.toString() === selectedProgram.processorType
      );
      if (matchingProcessor) {
        setFormData(prev => ({
          ...prev,
          processorTypeDisplay: matchingProcessor.processorType
        }));
      }
    }
  }, [processorList, selectedProgram.processorType]);

  // Add useEffect to handle async decryption of access key
  useEffect(() => {
    const decryptAccessKey = async () => {
      if (selectedProgram.bankDetails?.accessKey && EncryptionUtil.isEncrypted(selectedProgram.bankDetails.accessKey)) {
        try {
          const decryptedAccessKey = await EncryptionUtil.decrypt(
            selectedProgram.bankDetails.accessKey,
            window.env.ICICI_INTERNAL_API_KEY
          )

          setDecryptedAccessKey(decryptedAccessKey)
          setFormData(prev => ({
            ...prev,
            bankDetails: {
              ...prev.bankDetails,
              accessKey: selectedProgram.bankDetails.accessKey // Keep encrypted value in form data
            }
          }))
        } catch (error) {
          console.error("Error decrypting access key:", error)
          // If decryption fails, keep the encrypted value
        }
      } else if (selectedProgram.bankDetails?.accessKey) {
        // If access key exists but is not encrypted, store it as both encrypted and decrypted value
        setDecryptedAccessKey(selectedProgram.bankDetails.accessKey)
      }
    }

    decryptAccessKey()

  }, [selectedProgram.bankDetails?.accessKey])

  console.log("Bank Details Access Key", selectedProgram.bankDetails?.accessKey)
  // Add function to handle access key show/hide with proper decryption
  const handleAccessKeyToggle = async () => {
    if (!showEncryptedAccessKey) {
      // Showing the key - need to decrypt if it's encrypted
      const currentAccessKey = formData.bankDetails.accessKey
      if (currentAccessKey && EncryptionUtil.isEncrypted(currentAccessKey)) {
        try {
          const decrypted = await EncryptionUtil.decrypt(
            currentAccessKey,
            window.env.ICICI_INTERNAL_API_KEY
          )
          setDecryptedAccessKey(decrypted)
        } catch (error) {
          console.error("Error decrypting access key:", error)
          // If decryption fails, show the encrypted value
          setDecryptedAccessKey(currentAccessKey)
        }
      } else {
        // Not encrypted, show as is
        setDecryptedAccessKey(currentAccessKey || "")
      }
    }
    setShowEncryptedAccessKey(!showEncryptedAccessKey)
  }



  /* ------------------------------ Event Handlers ---------------------------- */


  const readFileAsUrl = (file: File | string): string => {
    if (typeof file === "string") {
      return file
    }
    return URL.createObjectURL(file)
  }

  const validateData = () => {
    let hasError = false
    const errors: Record<string, string> = {}

    if (!formData.programCode) {
      hasError = true
      errors.programCode = "Please enter a valid program code"
    } else if (
      getProcessorBrand(formData.processorTypeDisplay) === 'YESBANK' &&
      formData.programCode.length !== 7
    ) {
      hasError = true
      errors.programCode = "Program code must be exactly 7 characters for YesBank programs"
    }

    if (!formData.programName) {
      hasError = true
      errors.programName = "Please enter a valid program name"
    }

    if (!formData.displayName) {
      hasError = true
      errors.displayName = "Please enter a valid display name"
    }

    if (!formData.description) {
      hasError = true
      errors.description = "Please enter a valid description"
    }

    if (!formData.logo) {
      hasError = true
      errors.logo = "Please upload a valid logo file"
    }

    if (!formData.brandImage) {
      hasError = true
      errors.brandImage = "Please upload a valid brand image file"
    }

    if (!formData.voucherImage) {
      hasError = true
      errors.voucherImage = "Please upload a valid voucher image file"
    }

    if (!formData.currency) {
      hasError = true
      errors.currency = "Please select a currency"
    }

    if (!formData.processorType) {
      hasError = true
      errors.processorType = "Please select a processor type"
    }

    if (brandHasIssuanceType(formData.processorTypeDisplay) && !formData.issuanceType) {
      hasError = true
      errors.issuanceType = "Please select an issuance type"
    }

    if (!formData.tagline) {
      hasError = true
      errors.tagline = "Please enter a tag line"
    }

    if (!formData.receiverHowToUseInstructions) {
      hasError = true
      errors.receiverHowToUseInstructions = "Please enter a valid receiver how to use instructions"
    }

    if (!formData.standardTerms) {
      hasError = true
      errors.standardTerms = "Please enter a valid standard terms"
    }

    if (!formData.maximumAmount) {
      hasError = true
      errors.maximumAmount = "Please enter a valid maximum amount"
    }

    if (!formData.minimumAmount) {
      hasError = true
      errors.minimumAmount = "Please enter a valid minimum amount"
    }

    if (formData.denominationType === "fixed") {
      if (!formData.denominationAmount || formData.denominationAmount.length === 0) {
        hasError = true
        errors.denominationAmount = "Please enter at least one denomination amount"
      }
    } else {
      if (!formData.denominationMaxAmount) {
        hasError = true
        errors.denominationMaxAmount = "Please enter a valid denomination maximum amount"
      }

      if (!formData.denominationMinAmount) {
        hasError = true
        errors.denominationMinAmount = "Please enter a valid denomination minimum amount"
      }

    }

    // Bank & redemption details validation for processors that require them
    if (requiresBankDetails(formData.processorTypeDisplay)) {

      // Use the reusable bank details validation
      const bankDetailsHasError = validateBankDetails(formData.processorTypeDisplay, formData, errors)
      if (bankDetailsHasError) {
        hasError = true
      }
    }
    return { hasError, errors }
  }

  const handleAddProgram = async (programData: CreateProgramPayload) => {
    const formDataToSend = new FormData()

    // Helper function to check if a field has been modified
    const isFieldModified = (field: string, newValue: any, oldValue: any) => {
      if (field === 'logo' || field === 'brandImage' || field === 'voucherImage') {
        return newValue !== null && newValue !== oldValue;
      }
      return newValue !== oldValue;
    };

    // Only append modified fields
    if (isFieldModified('programName', programData.programName, selectedProgram.programName)) {
      formDataToSend.append("programName", programData.programName);
    }
    if (isFieldModified('programCode', programData.programCode, selectedProgram.programCode)) {
      formDataToSend.append("programCode", programData.programCode);
    }
    if (isFieldModified('description', programData.description, selectedProgram.description)) {
      formDataToSend.append("description", programData.description);
    }
    if (isFieldModified('standardTerms', programData.standardTerms, selectedProgram.standardTerms)) {
      formDataToSend.append("standardTerms", programData.standardTerms);
    }
    if (isFieldModified('specificTerms', programData.specificTerms, selectedProgram.specificTerms)) {
      formDataToSend.append("specificTerms", programData.specificTerms);
    }
    if (isFieldModified('displayName', programData.displayName, selectedProgram.displayName)) {
      formDataToSend.append("displayName", programData.displayName);
    }
    if (isFieldModified('currency', programData.currency, selectedProgram.currency)) {
      formDataToSend.append("currency", programData.currency);
    }
    if (isFieldModified('processorType', programData.processorType, selectedProgram.processorType)) {
      formDataToSend.append("processorType", programData.processorType);
      // Handle processorTypeDisplay if it's also modified
      if (isFieldModified('processorTypeDisplay', programData.processorTypeDisplay, selectedProgram.processorTypeDisplay)) {
        formDataToSend.append("processorTypeDisplay", programData.processorTypeDisplay);
      }
    }
    if (isFieldModified('issuanceType', programData.issuanceType, selectedProgram.issuanceType)) {
      formDataToSend.append("issuanceType", programData.issuanceType);
    }
    if (isFieldModified('tagline', programData.tagline, selectedProgram.tagline)) {
      formDataToSend.append("tagline", programData.tagline);
    }
    if (isFieldModified('cardDescription', programData.cardDescription, selectedProgram.cardDescription)) {
      formDataToSend.append("cardDescription", programData.cardDescription);
    }
    if (isFieldModified('receiverInstructions', programData.receiverInstructions, selectedProgram.receiverInstructions)) {
      formDataToSend.append("receiverInstructions", programData.receiverInstructions);
    }
    if (isFieldModified('receiverHowToUseInstructions', programData.receiverHowToUseInstructions, selectedProgram.receiverHowToUseInstructions)) {
      formDataToSend.append("receiverHowToUseInstructions", programData.receiverHowToUseInstructions);
    }
    if (isFieldModified('callToAction', programData.callToAction, selectedProgram.callToAction)) {
      formDataToSend.append("callToAction", programData.callToAction);
    }
    if (isFieldModified('brandImageAlt', programData.brandImageAlt, selectedProgram.brandImageAlt)) {
      formDataToSend.append("brandImageAlt", programData.brandImageAlt);
    }
    if (isFieldModified('maximumAmount', programData.maximumAmount, selectedProgram.maximumAmount)) {
      formDataToSend.append("maximumAmount", String(programData.maximumAmount));
    }
    if (isFieldModified('minimumAmount', programData.minimumAmount, selectedProgram.minimumAmount)) {
      formDataToSend.append("minimumAmount", String(programData.minimumAmount));
    }


    // Handle denomination fields
    if (isFieldModified('denominationType', programData.denominationType, selectedProgram.denominationType)) {
      formDataToSend.append("denominationType", programData.denominationType || "");
    }
    if (programData.denominationType === "fixed") {
      if (isFieldModified('denominationAmount', programData.denominationAmount, selectedProgram.denominationAmount)) {
        formDataToSend.append("denominationAmount", JSON.stringify(programData.denominationAmount));
      }
    } else {
      if (isFieldModified('denominationMaxAmount', programData.denominationMaxAmount, selectedProgram.denominationMaxAmount)) {
        formDataToSend.append("denominationMaxAmount", String(programData.denominationMaxAmount));
      }
      if (isFieldModified('denominationMinAmount', programData.denominationMinAmount, selectedProgram.denominationMinAmount)) {
        formDataToSend.append("denominationMinAmount", String(programData.denominationMinAmount));
      }
    }

    // Handle file uploads
    if (programData.logo && isFieldModified('logo', programData.logo, selectedProgram.logo)) {
      formDataToSend.append("logo", programData.logo);
    }
    if (programData.brandImage && isFieldModified('brandImage', programData.brandImage, selectedProgram.brandImage)) {
      formDataToSend.append("brandImage", programData.brandImage);
    }
    if (programData.voucherImage && isFieldModified('voucherImage', programData.voucherImage, selectedProgram.voucherImage)) {
      formDataToSend.append("voucherImage", programData.voucherImage);
    }


    // Handle bank-related fields for processors that require them
    if (requiresBankDetails(programData.processorTypeDisplay)) {

      const cleanBankDetails: BankDetails = {}

      if (isFieldModified('period', programData.period, selectedProgram.period)) {
        formDataToSend.append("period", String(programData.period));
      }


      if (getProcessorBrand(programData.processorTypeDisplay) === 'ICICI') {
        formDataToSend.append("redemptionType", programData.redemptionType);
        formDataToSend.append("merchantId", programData.merchantId);
        formDataToSend.append("subMerchantId", programData.subMerchantId);

        cleanBankDetails.mcc = programData.bankDetails.mcc
        cleanBankDetails.payerVa = programData.bankDetails.payerVa
        cleanBankDetails.purposeCode = programData.bankDetails.purposeCode
        cleanBankDetails.mandateType = programData.bankDetails.mandateType


        if (isFieldModified('accessKey', programData.bankDetails.accessKey, selectedProgram.bankDetails.accessKey)) {
          cleanBankDetails.accessKey = await EncryptionUtil.encrypt(programData.bankDetails.accessKey || "", window.env.ICICI_INTERNAL_API_KEY)
        }else{
          cleanBankDetails.accessKey = selectedProgram.bankDetails.accessKey
        }

        if (programData.bankDetails.mandateType === "04") {
          cleanBankDetails.payeeVpa = programData.bankDetails.payeeVpa || ""
        }
      }
      else if (getProcessorBrand(programData.processorTypeDisplay) === 'YESBANK') {
        cleanBankDetails.purposeCode = programData.bankDetails.purposeCode
        cleanBankDetails.mandateName = programData.bankDetails.mandateName
        cleanBankDetails.amountRule = programData.bankDetails.amountRule
        cleanBankDetails.voucherCategory = programData.bankDetails.voucherCategory
        cleanBankDetails.recurrencePattern = programData.bankDetails.recurrencePattern
        cleanBankDetails.payerRevocable = programData.bankDetails.payerRevocable
        cleanBankDetails.preAuth = programData.bankDetails.preAuth
        if (programData.bankDetails.clientIdentifier) {
          cleanBankDetails.clientIdentifier = programData.bankDetails.clientIdentifier
        }
        if (programData.bankDetails.refurl) {
          cleanBankDetails.refurl = programData.bankDetails.refurl
        }
      }
      else if (isIciciCbdcProcessor(programData.processorTypeDisplay)) {
        // ICICI CBDC: sponsor-details, purpose, mcc, language, location are
        // stored as plain bank details, same as any other processor's
        // bankDetails - program update no longer calls any ICICI CBDC bank
        // API. Location rows are stored in the form as human-readable
        // state/city names for a usable UI; they're converted to the bank's
        // numeric {city,state,bufferZone} codes here, right before
        // submission - same conversion as AddPrograms.tsx.
        cleanBankDetails.sponsorId = programData.bankDetails.sponsorId
        cleanBankDetails.sponsorName = programData.bankDetails.sponsorName
        cleanBankDetails.purpose = programData.bankDetails.purpose
        cleanBankDetails.mcc = programData.bankDetails.mcc
        if (programData.bankDetails.language) {
          cleanBankDetails.language = programData.bankDetails.language
        }
        if (programData.bankDetails.location && programData.bankDetails.location.length > 0) {
          cleanBankDetails.location = toIciciCbdcLocationPayload(programData.bankDetails.location)
        }
      }
      else if (getProcessorBrand(programData.processorTypeDisplay) === 'MYP') {
        Object.assign(cleanBankDetails, buildMypCleanBankDetails(programData.bankDetails, selectedProgram.bankDetails))
      }
      formDataToSend.append("bankDetails", JSON.stringify(cleanBankDetails));
    }

    try {
      const response = await dispatch(
        updateProgram({ formData: formDataToSend, programId: programId }),
      )
      if (updateProgram.fulfilled.match(response)) {
        showComponent(null)
      }
    } catch (error) {
      console.error("Error creating program:", error)
    }
  }

  /**
   * "Re-sync with MYP" saves whatever's currently in the form (e.g. edited
   * merchantRules) first, then provisions with MYP - a single click does
   * both. This matters because provisionMypForProgram (the MYP-only call)
   * takes no body and reads the program's bankDetails from the DB, not from
   * this component's in-memory formData: clicking it without saving first
   * would just re-push whatever was last saved, silently ignoring any
   * unsaved edit sitting in the form (including a merchantRules change).
   * Unlike the main "Update Program" submit, this never closes the page -
   * you stay put and see the resulting provisioned/failed status inline.
   */
  const handleProvisionClick = async () => {
    setSyncingMyp(true)
    try {
      const bankDetailsFormData = new FormData()
      bankDetailsFormData.append(
        "bankDetails",
        JSON.stringify(buildMypCleanBankDetails(formData.bankDetails, selectedProgram.bankDetails)),
      )

      const saveResponse = await dispatch(
        updateProgram({ formData: bankDetailsFormData, programId }),
      )
      if (!updateProgram.fulfilled.match(saveResponse)) {
        return
      }

      await dispatch(provisionMypForProgram(programId))
    } finally {
      setSyncingMyp(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const { hasError, errors } = validateData()
    if (hasError) {
      setFormErrors(errors)

      // Find first input with error and scroll to it
      const firstErrorField = Object.keys(errors)[0]

      // Map error keys to input IDs
      const getInputId = (errorKey: string) => {
        const capitalizedErrorKey = errorKey.charAt(0).toUpperCase() + errorKey.slice(1);
        if (capitalizedErrorKey.startsWith("BankDetails")) {
          return "edit" + capitalizedErrorKey.replace("BankDetails", "")
        }
        return "edit" + capitalizedErrorKey
      }



      const errorElement = document.getElementById(getInputId(firstErrorField))
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" })
        errorElement.focus()
      }
      return
    }

    handleAddProgram({
      ...formData,
      principalId: Number(principal.principalId),
    })
  }

  const handlePurposeCodeChange = (purposeCode: string) => {
    const selectedPurpose = purposeCodeList.find(p => p.code === purposeCode)
    if (selectedPurpose) {
      const { bankDetailsPurposeCode, ...rest } = formErrors
      setFormErrors(rest)

      // Clear current MCC selection and trigger fetch of MCC mappings for this purpose code
      setMccList([])
      setFormData({
        ...formData,
        bankDetails: { ...formData.bankDetails, purposeCode: purposeCode, mcc: "" },
      })

      dispatch(fetchPurposeCodeByCode(purposeCode))
    }
  }

  // ICICI CBDC MCC dropdown: lazily fetch a purpose code's MCCs the first
  // time it's expanded (the purpose code list endpoint doesn't include them).
  const handleExpandPurposeCode = (code: string) => {
    setLoadingMccsForPurposeCode(code)
    dispatch(fetchPurposeCodeByCode(code))
  }

  /* ------------------------------ UI Handlers ---------------------------- */

  return (
    <div className="max-w-7xl mx-auto px-8">
      <form onSubmit={handleSubmit} className="space-y-8">
        {programError && (
          <div
            id="edit_ProgramError"
            className="p-4 mb-6 text-sm text-red-700 bg-red-100 rounded-lg"
            role="alert"
          >
            {programError}
          </div>
        )}

        {/* Basic Details Section */}
        <div className="border rounded-lg p-6 bg-white ">
          <h2 className="text-lg font-semibold mb-4">Basic Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label htmlFor="editProgramName" className="block text-sm font-medium text-gray-700">
                Program Name
              </label>
              <input
                type="text"
                id="editProgramName"
                placeholder="Enter program name"
                value={formData.programName}
                onChange={e => {
                  const { programName, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, programName: e.target.value })
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.programName ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.programName && (
                <p className="mt-1 text-sm text-red-600">{formErrors.programName}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="editDisplayName" className="block text-sm font-medium text-gray-700">
                Display Name
              </label>
              <input
                type="text"
                id="editDisplayName"
                placeholder="Enter display name"
                value={formData.displayName}
                onChange={e => {
                  const { displayName, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, displayName: e.target.value })
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.displayName ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.displayName && (
                <p className="mt-1 text-sm text-red-600">{formErrors.displayName}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="editProgramCode" className="block text-sm font-medium text-gray-700">
                Program Code
                {['YESBANK_DIRECT', 'YESBANK_INDIRECT'].includes(formData.processorTypeDisplay) && (
                  <span className="ml-2 text-xs text-gray-400 font-normal">(exactly 7 characters)</span>
                )}
              </label>
              <input
                type="text"
                id="editProgramCode"
                placeholder="Enter program code"
                maxLength={['YESBANK_DIRECT', 'YESBANK_INDIRECT'].includes(formData.processorTypeDisplay) ? 7 : undefined}
                value={formData.programCode}
                onChange={e => {
                  const { programCode, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, programCode: e.target.value })
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.programCode ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.programCode && (
                <p className="mt-1 text-sm text-red-600">{formErrors.programCode}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="editDescription" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="editDescription"
                placeholder="Enter description"
                value={formData.description}
                onChange={e => {
                  const { description, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, description: e.target.value })
                }}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.description && (
                <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
              )}
            </div>

            {/* Program Settings fields moved here */}
            <div className="space-y-2">
              <label htmlFor="editProcessorType" className="block text-sm font-medium text-gray-700">
                Processor Type
              </label>
              <div className="relative">
                <select
                  id="editProcessorType"
                  value={formData.processorTypeDisplay}
                  onChange={e => {
                    const selectedProcessor = processorList.find(p => p.processorType === e.target.value)
                    const { processorType, issuanceType, ...rest } = formErrors
                    setFormErrors(rest)
                    setFormData({
                      ...formData,
                      processorType: selectedProcessor?.processorId.toString() || "",
                      processorTypeDisplay: e.target.value,
                      // Changing brand invalidates any previously chosen
                      // issuance lifecycle - force the user to re-pick it.
                      issuanceType: "",
                      // MCC is a single string for ICICI/YESBank but a multi-select
                      // array for ICICI CBDC - reset it to the right shape when the
                      // processor type changes so stale data isn't submitted.
                      bankDetails: {
                        ...formData.bankDetails,
                        mcc: "",
                      },
                    })
                  }}
                  className={`block w-full px-3 py-2 border ${formErrors.processorType ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700  focus:outline-none appearance-none transition`}
                >
                  <option value="">Select Processor Type</option>
                  {processorList.map(processor => (
                    <option key={processor.processorId} value={processor.processorType}>
                      {processor.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              {formErrors.processorType && (
                <p className="mt-1 text-sm text-red-600">{formErrors.processorType}</p>
              )}
            </div>

            {brandHasIssuanceType(formData.processorTypeDisplay) && (
              <div className="space-y-2">
                <label htmlFor="editIssuanceType" className="block text-sm font-medium text-gray-700">
                  Issuance Type
                </label>
                <div className="relative">
                  <select
                    id="editIssuanceType"
                    value={formData.issuanceType}
                    onChange={e => {
                      const { issuanceType, ...rest } = formErrors
                      setFormErrors(rest)
                      setFormData({ ...formData, issuanceType: e.target.value as typeof formData.issuanceType })
                    }}
                    className={`block w-full px-3 py-2 border ${formErrors.issuanceType ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition`}
                  >
                    <option value="">Select issuance type</option>
                    <option value="direct">Direct</option>
                    <option value="indirect">Indirect</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {formErrors.issuanceType && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.issuanceType}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="editTagline" className="block text-sm font-medium text-gray-700">
                Tagline
              </label>
              <input
                type="text"
                id="editTagline"
                placeholder="Enter tagline"
                value={formData.tagline}
                onChange={e => {
                  const { tagline, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, tagline: e.target.value })
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.tagline ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.tagline && (
                <p className="mt-1 text-sm text-red-600">{formErrors.tagline}</p>
              )}
            </div>
            <div className="space-y-2">
              <label htmlFor="editCallToAction" className="block text-sm font-medium text-gray-700">
                Call to Action
              </label>
              <input
                type="text"
                id="editCallToAction"
                placeholder="Enter call to action"
                value={formData.callToAction}
                onChange={e => {
                  const { callToAction, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, callToAction: e.target.value })
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.callToAction ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.callToAction && (
                <p className="mt-1 text-sm text-red-600">{formErrors.callToAction}</p>
              )}
            </div>
            <div className="space-y-2">
              <label htmlFor="editBrandImageAlt" className="block text-sm font-medium text-gray-700">
                Brand Image Alt
              </label>
              <input
                type="text"
                id="editBrandImageAlt"
                placeholder="Enter brand image alt"
                value={formData.brandImageAlt}
                onChange={e => {
                  const { brandImageAlt, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, brandImageAlt: e.target.value })
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.brandImageAlt ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.brandImageAlt && (
                <p className="mt-1 text-sm text-red-600">{formErrors.brandImageAlt}</p>
              )}
            </div>
            <div className="space-y-2">
              <label htmlFor="editMaximumAmount" className="block text-sm font-medium text-gray-700">
                Maximum Amount
              </label>
              <input
                type="number"
                id="editMaximumAmount"
                placeholder="Enter maximum amount"
                value={formData.maximumAmount}
                onChange={e => {
                  const { maximumAmount, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, maximumAmount: parseInt(e.target.value) })
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.maximumAmount ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.maximumAmount && (
                <p className="mt-1 text-sm text-red-600">{formErrors.maximumAmount}</p>
              )}
            </div>
            <div className="space-y-2">
              <label htmlFor="editMinimumAmount" className="block text-sm font-medium text-gray-700">
                Minimum Amount
              </label>
              <input
                type="number"
                id="editMinimumAmount"
                placeholder="Enter minimum amount"
                value={formData.minimumAmount}
                onChange={e => {
                  const { minimumAmount, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, minimumAmount: parseInt(e.target.value) })
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.minimumAmount ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.minimumAmount && (
                <p className="mt-1 text-sm text-red-600">{formErrors.minimumAmount}</p>
              )}
            </div>

          </div>
        </div>

        {/* Images Section */}
        <div className="border rounded-lg p-6 bg-white ">
          <h2 className="text-lg font-semibold mb-4">Images</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Logo */}
            <div className="flex flex-col items-start justify-between h-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
              <div className="relative w-full h-48 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50 mb-2">
                {formData.logo ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prevState => ({ ...prevState, logo: null }))
                        const fileInput = document.getElementById("editLogo") as HTMLInputElement
                        if (fileInput) fileInput.value = ""
                      }}
                      className="absolute top-2 right-2 p-1 bg-white rounded-full  hover:bg-gray-100 z-10"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>
                    <img src={readFileAsUrl(formData.logo)} alt="" className="w-full h-full object-contain" />
                  </>
                ) : (
                  <span className="text-gray-400">No image</span>
                )}
              </div>
              <input
                type="file"
                id="editLogo"
                accept="image/*"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const { logo, ...rest } = formErrors
                  setFormErrors(rest)
                  if (e.target.files && e.target.files[0]) {
                    setFormData({ ...formData, logo: e.target.files[0] })
                  }
                }}
                className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand file:text-white hover:file:bg-brand/80"
              />
              {formErrors.logo && (
                <p className="mt-1 text-sm text-red-600">{formErrors.logo}</p>
              )}
            </div>
            {/* Banner Image */}
            <div className="flex flex-col items-start justify-between h-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">Banner Image</label>
              <div className="relative w-full h-48 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50 mb-2">
                {formData.brandImage ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prevState => ({ ...prevState, brandImage: null }))
                        const fileInput = document.getElementById("editBrandImage") as HTMLInputElement
                        if (fileInput) fileInput.value = ""
                      }}
                      className="absolute top-2 right-2 p-1 bg-white rounded-full  hover:bg-gray-100 z-10"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>
                    <img src={readFileAsUrl(formData.brandImage)} alt="" className="w-full h-full object-cover" />
                  </>
                ) : (
                  <span className="text-gray-400">No image</span>
                )}
              </div>
              <input
                type="file"
                id="editBrandImage"
                accept="image/*"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const { brandImage, ...rest } = formErrors
                  setFormErrors(rest)
                  if (e.target.files && e.target.files[0]) {
                    setFormData({ ...formData, brandImage: e.target.files[0] })
                  }
                }}
                className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand file:text-white hover:file:bg-brand/80"
              />
              {formErrors.brandImage && (
                <p className="mt-1 text-sm text-red-600">{formErrors.brandImage}</p>
              )}
            </div>
            {/* Voucher Image */}
            <div className="flex flex-col items-start justify-between h-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">Voucher Image</label>
              <div className="relative w-full h-48 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50 mb-2">
                {formData.voucherImage ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prevState => ({ ...prevState, voucherImage: null }))
                        const fileInput = document.getElementById("editVoucherImage") as HTMLInputElement
                        if (fileInput) fileInput.value = ""
                      }}
                      className="absolute top-2 right-2 p-1 bg-white rounded-full  hover:bg-gray-100 z-10"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>
                    <img src={readFileAsUrl(formData.voucherImage)} alt="" className="w-full h-full object-contain" />
                  </>
                ) : (
                  <span className="text-gray-400">No image</span>
                )}
              </div>
              <input
                type="file"
                id="editVoucherImage"
                accept="image/*"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const { voucherImage, ...rest } = formErrors
                  setFormErrors(rest)
                  if (e.target.files && e.target.files[0]) {
                    setFormData({ ...formData, voucherImage: e.target.files[0] })
                  }
                }}
                className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand file:text-white hover:file:bg-brand/80"
              />
              {formErrors.voucherImage && (
                <p className="mt-1 text-sm text-red-600">{formErrors.voucherImage}</p>
              )}
            </div>
          </div>
        </div>

        {/* Bank & Redemption Details Section */}
        {requiresBankDetails(formData.processorTypeDisplay) && <div className="border rounded-lg p-6 bg-white ">
          <h2 className="text-lg font-semibold mb-4">Bank & Redemption Details</h2>


          {/* Add the new reusable bank details section */}
          {renderBankDetailsSection(formData.processorTypeDisplay, {
            formData,
            formErrors: formErrors as Record<string, string>,
            setFormData,
            setFormErrors,
            showAccessKey: showEncryptedAccessKey,
            setShowAccessKey: handleAccessKeyToggle,
            decryptedAccessKey,
            mccList,
            purposeCodeList: purposeCodeList.map(p => ({ purposeCode: p.code, description: p.description })),
            purposeCodeOptions: purposeCodeList,
            fieldPrefix: "edit",
            handlePurposeCodeChange,
            onExpandPurposeCode: handleExpandPurposeCode,
            loadingMccsForPurposeCode,
            programId,
            onProvision: handleProvisionClick,
            provisionLoading: syncingMyp || mypProvisionLoading,
            provisionError: mypProvisionError,
          })}

        </div>

        }
        {/* Denomination Details Section */}
        <div className="border rounded-lg p-6 bg-white ">
          <h2 className="text-lg font-semibold mb-4">Denomination Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label htmlFor="editDenominationType" className="block text-sm font-medium text-gray-700">
                Denomination Type
              </label>
              <div className="relative">
                <select
                  id="editDenominationType"
                  value={formData.denominationType}
                  onChange={e => {
                    const { denominationType, ...rest } = formErrors
                    setFormErrors(rest)
                    setFormData({ ...formData, denominationType: e.target.value })
                  }}
                  className={`block w-full px-3 py-2 border ${formErrors.denominationType ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700  focus:outline-none appearance-none transition`}
                >
                  <option value="">Select denomination type</option>
                  <option value="fixed">Fixed</option>
                  <option value="range">Range</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              {formErrors.denominationType && (
                <p className="mt-1 text-sm text-red-600">{formErrors.denominationType}</p>
              )}
            </div>

            {formData.denominationType === "fixed" && (
              <div className="space-y-2">
                <label htmlFor="editDenominationAmount" className="block text-sm font-medium text-gray-700">
                  Denomination Amount
                </label>
                <input
                  type="number"
                  id="editDenominationAmount"
                  placeholder="Enter denomination amount"
                  value={formData.denominationAmount[0]}
                  onChange={e => {
                    const { denominationAmount, ...rest } = formErrors
                    setFormErrors(rest)
                    setFormData({
                      ...formData,
                      denominationAmount: [parseInt(e.target.value)],
                    })
                  }}
                  className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.denominationAmount ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {formErrors.denominationAmount && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.denominationAmount}</p>
                )}
              </div>
            )}

            {formData.denominationType === "range" && (
              <>
                <div className="space-y-2">
                  <label htmlFor="editDenominationMinAmount" className="block text-sm font-medium text-gray-700">
                    Minimum Amount
                  </label>
                  <input
                    type="number"
                    id="editDenominationMinAmount"
                    placeholder="Enter minimum amount"
                    value={formData.denominationMinAmount}
                    onChange={e => {
                      const { denominationMinAmount, ...rest } = formErrors
                      setFormErrors(rest)
                      setFormData({
                        ...formData,
                        denominationMinAmount: parseInt(e.target.value),
                      })
                    }}
                    className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.denominationMinAmount ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {formErrors.denominationMinAmount && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.denominationMinAmount}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="editDenominationMaxAmount" className="block text-sm font-medium text-gray-700">
                    Maximum Amount
                  </label>
                  <input
                    type="number"
                    id="editDenominationMaxAmount"
                    placeholder="Enter maximum amount"
                    value={formData.denominationMaxAmount}
                    onChange={e => {
                      const { denominationMaxAmount, ...rest } = formErrors
                      setFormErrors(rest)
                      setFormData({
                        ...formData,
                        denominationMaxAmount: parseInt(e.target.value),
                      })
                    }}
                    className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.denominationMaxAmount ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {formErrors.denominationMaxAmount && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.denominationMaxAmount}</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Terms & Instructions Section */}
        <div className="border rounded-lg p-6 bg-white ">
          <h2 className="text-lg font-semibold mb-4">Terms & Instructions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label htmlFor="editStandardTerms" className="block text-sm font-medium text-gray-700">
                Standard Terms
              </label>
              <textarea
                id="editStandardTerms"
                placeholder="Enter standard terms"
                value={formData.standardTerms}
                onChange={e => {
                  const { standardTerms, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, standardTerms: e.target.value })
                }}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.standardTerms ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.standardTerms && (
                <p className="mt-1 text-sm text-red-600">{formErrors.standardTerms}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="editSpecificTerms" className="block text-sm font-medium text-gray-700">
                Specific Terms
              </label>
              <textarea
                id="editSpecificTerms"
                placeholder="Enter specific terms"
                value={formData.specificTerms}
                onChange={e => {
                  const { specificTerms, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, specificTerms: e.target.value })
                }}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.specificTerms ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.specificTerms && (
                <p className="mt-1 text-sm text-red-600">{formErrors.specificTerms}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="editReceiverInstructions" className="block text-sm font-medium text-gray-700">
                Receiver Instructions
              </label>
              <textarea
                id="editReceiverInstructions"
                placeholder="Enter receiver instructions"
                value={formData.receiverInstructions}
                onChange={e => {
                  const { receiverInstructions, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, receiverInstructions: e.target.value })
                }}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.receiverInstructions ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.receiverInstructions && (
                <p className="mt-1 text-sm text-red-600">{formErrors.receiverInstructions}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="editReceiverHowToUseInstructions" className="block text-sm font-medium text-gray-700">
                Receiver How To Use Instructions
              </label>
              <textarea
                id="editReceiverHowToUseInstructions"
                placeholder="Enter receiver how to use instructions"
                value={formData.receiverHowToUseInstructions}
                onChange={e => {
                  const { receiverHowToUseInstructions, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, receiverHowToUseInstructions: e.target.value })
                }}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.receiverHowToUseInstructions ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.receiverHowToUseInstructions && (
                <p className="mt-1 text-sm text-red-600">{formErrors.receiverHowToUseInstructions}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-between gap-4 mt-8 pb-8">
          <button
            type="button"
            onClick={() => showComponent(null)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-md hover:bg-brand/80 focus:outline-none"
          >
            Update Program
          </button>
        </div>
      </form>
    </div>
  )
}

export default EditPrograms
